import React, { useState, useMemo, useCallback } from "react";
import { Card, CardBody, Button, Badge, Progress } from "reactstrap";

import GradeHeader from "../components/GradeHeader";
import GradeGrid from "../components/GradeGrid";
import ClassPlanModal from "./components/ClassPlanModal";

import { ContentLoader, Breadcrumb } from "../../../components/Common";
import { usePlanningData } from "./hooks/usePlanningData";
import { getStartOfWeek } from "../../../utils/date";

/**
 * PlanningPage
 * Grade de Planejamento Inteligente de Aulas.
 */
const PlanningPage = () => {
    // Parâmetros de Data e Visão
    const [referenceDate, setReferenceDate] = useState(new Date());
    const [view, setView] = useState("week");
    const [turn, setTurn] = useState("all");

    // Dados de planejamento
    const {
        sessions,
        sessionGroups,
        loading: loadingData,
        generating,
        progress,
        generateAllPlans,
    } = usePlanningData({ referenceDate, view });

    // Cálculo do Início da Semana
    const weekStart = useMemo(() => getStartOfWeek(referenceDate), [referenceDate]);

    // Estado do Modal
    const [selectedSession, setSelectedSession] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSelectSchedule = useCallback((schedule) => {
        setSelectedSession(schedule);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedSession(null);
    }, []);

    // Grupos sem plano
    const groupsWithoutPlan = useMemo(() => {
        return sessionGroups.filter(g => !g.hasPlans);
    }, [sessionGroups]);

    // Progresso em %
    const progressPercent = progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0;

    if (loadingData && sessions.length === 0) {
        return <ContentLoader loading={true} message="Carregando planejamento..." />;
    }

    return (
        <div className="h-100 w-100 pb-5">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Planejamento de Aulas" />
            </div>

            <div className="border-top mb-3"></div>

            <Card className="mb-4 rounded-0 shadow-sm border-0">
                <CardBody className="pb-2">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <i className="mdi mdi-clipboard-text-outline font-size-22 text-primary"></i>
                            <h5 className="mb-0 fw-bold">Painel de Geração</h5>
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                            {groupsWithoutPlan.length > 0 && !generating && (
                                <Badge color="warning" className="d-flex align-items-center gap-1 py-2 px-3 font-size-12">
                                    <i className="mdi mdi-alert-circle-outline"></i>
                                    {groupsWithoutPlan.length} turma(s) sem plano
                                </Badge>
                            )}

                            <Button
                                color="primary"
                                size="sm"
                                disabled={generating || sessionGroups.length === 0}
                                onClick={generateAllPlans}
                                className="d-flex align-items-center gap-1"
                            >
                                <i className="mdi mdi-auto-fix"></i>
                                {generating ? "Gerando..." : "Gerar Planejamento"}
                            </Button>
                        </div>
                    </div>

                    {/* Barra de Progresso */}
                    {generating && (
                        <div className="mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="font-size-12 text-muted">
                                    <i className="mdi mdi-loading mdi-spin me-1"></i>
                                    Atualizando planejamento...
                                </span>
                                <span className="font-size-12 fw-bold text-primary">
                                    {progress.current}/{progress.total} turma(s)
                                </span>
                            </div>
                            <Progress
                                value={progressPercent}
                                color="primary"
                                animated
                                striped
                                style={{ height: "6px" }}
                            />
                        </div>
                    )}

                    <GradeHeader
                        turn={turn}
                        onTurnChange={setTurn}
                        view={view}
                        onViewChange={setView}
                        referenceDate={referenceDate}
                        onReferenceDateChange={setReferenceDate}
                    />
                </CardBody>
            </Card>

            {/* Grid */}
            <Card className="rounded-0 shadow-sm border-0">
                <CardBody>
                    {!loadingData && sessions.length === 0 && (
                        <div className="text-center py-5">
                            <i className="mdi mdi-clipboard-text-off-outline font-size-48 text-muted d-block mb-3"></i>
                            <h5 className="text-muted">Nenhuma turma avaliativa encontrada</h5>
                            <p className="text-muted font-size-13 mb-0">
                                Cadastre turmas com atividades que possuam o <strong>Sistema de Avaliação</strong> ativado
                                para que apareçam aqui.
                            </p>
                        </div>
                    )}

                    <GradeGrid
                        turn={turn}
                        view={view}
                        referenceDate={referenceDate}
                        weekStart={weekStart}
                        schedules={sessions}
                        loading={loadingData}
                        onSelectSchedule={handleSelectSchedule}
                    />
                </CardBody>
            </Card>

            {/* Modal de Planejamento */}
            <ClassPlanModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                session={selectedSession}
            />
        </div>
    );
};

export default PlanningPage;
